import type { wave } from '@round/api';

export type PostMetrics = {
    viewCount: number | null;
    refreshedAt: string | null;
    cpmViews: number | null;
    cpmEarned: string | null;
    cpmUpdatedAt: string | null;
    currencySymbol: string;
};

/** A post as an offer card shows it, whichever reads the page has. */
export type OfferPost = {
    id: number;
    contractId: number;
    /** Null while the post's account is unresolved. */
    handle: string | null;
    avatarUrl: string | null;
    platform: wave.InvitePlatform;
    url: string;
    reviewStatus: wave.SubmissionReviewStatus;
    reviewReason: string;
    metrics: PostMetrics | null;
};

type GetOfferPostsParams = {
    contracts: wave.CreatorContract[];
    invite: wave.Invite;
    /** The signed-in creator's own post read (/me/posts/), which carries the metrics. */
    creatorPosts?: wave.CreatorPost[];
};

/**
 * Every post on the invite's contracts, from the contract read, with metrics added from the creator's
 * own post read by id. Un-authed pages don't have that read, so their posts come without metrics, as do
 * a signed-in page's while it loads or if it fails.
 */
export const getOfferPosts = ({ contracts, invite, creatorPosts }: GetOfferPostsParams): OfferPost[] =>
    contracts.flatMap((contract) =>
        contract.posts.map((post) => {
            const creatorPost = creatorPosts?.find((candidate) => candidate.id === post.id);
            return {
                id: post.id,
                contractId: contract.id,
                handle: post.handle,
                avatarUrl:
                    invite.allowed_accounts.find((account) => account.handle === post.handle)?.avatar_url ?? null,
                platform: invite.platform,
                url: post.post_url,
                reviewStatus: post.review_status,
                reviewReason: post.review_reason,
                metrics: creatorPost
                    ? {
                          viewCount: creatorPost.view_count,
                          refreshedAt: creatorPost.metrics_refreshed_at,
                          cpmViews: creatorPost.cpm_earnings_views,
                          cpmEarned: creatorPost.cpm_earnings_amount,
                          cpmUpdatedAt: creatorPost.cpm_earnings_updated_at,
                          currencySymbol: creatorPost.offer_currency_symbol,
                      }
                    : null,
            };
        })
    );
